import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { PermissionType } from '@app/core/models/permission';
import { ICoreState } from '@app/core/store/core.reducer';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type LogicType = 'AND' | 'OR';

@Directive({ selector: '[nbPermissions]' })
export class NbPermissionsDirective implements OnInit, OnDestroy {
  @Input()
  set nbPermissions(inputPermissions: PermissionType[]) {
    this.askedPermissions = inputPermissions;
    this.updateView();
  }

  @Input()
  set nbPermissionsLogic(logic: LogicType) {
    this.logic = logic || 'AND';
  }

  private logic: LogicType = 'AND';
  private userPermissions: PermissionType[] = [];
  private askedPermissions: PermissionType[] = [];

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(private templateRef: TemplateRef<any>, private viewContainer: ViewContainerRef, private store: Store) {}

  /**
   * subscribe to user permission states on directive initialized
   * changes in permission states will trigger updateView()
   */
  ngOnInit() {
    this.store
      .select<PermissionType[]>(({ core }: { core: ICoreState }) => {
        return core?.session?.permissions ?? [];
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((permissions: PermissionType[]) => {
        this.userPermissions = permissions;
        this.updateView();
      });
  }

  /**
   * unsubscribe to user permission states on directive destroyed
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * display/hide the attaching directive based on provided permissions
   */
  updateView() {
    this.viewContainer.clear();
    if (this.checkPermission()) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }

  /**
   * if logic is AND, asked permissions should be a subset of current user permissions
   * if logic is OR, user permissions have at least one of asked permissions
   */
  checkPermission() {
    if (this.logic === 'AND') {
      let allowed = true;
      for (const permit of this.askedPermissions) {
        if (this.userPermissions.indexOf(permit) < 0) {
          allowed = false;
        }
      }
      return allowed;
    } else if (this.logic === 'OR') {
      let allowed = false;
      for (const permit of this.askedPermissions) {
        if (this.userPermissions.indexOf(permit) >= 0) {
          allowed = true;
        }
      }
      return allowed;
    }
  }
}
